import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ApiError } from '../api/envelope';
import { clearHandoff, peekHandoff } from '../auth/handoff';
import { useAuth } from '../auth/context';
import { BrandMark } from '../components/BrandMark';
import { ErrorNote } from '../components/ErrorNote';
import { Spinner } from '../components/Spinner';
import './SignIn.css';

/**
 * The landing pad for `/sso?code=…&to=…` — the dashboard's "open Channels in a
 * new tab" link. The code was already lifted out of the address bar by
 * captureHandoff in main.tsx, so by the time this renders the URL is clean and
 * the credential lives only in memory.
 *
 * The exchange is fired from a ref-guarded effect rather than on every render:
 * the code is single-use, so StrictMode's second pass would otherwise burn it
 * and land the user on the expired-link screen in development only.
 */
export function Sso() {
  const { status, signInWithHandoff } = useAuth();
  const navigate = useNavigate();
  // Read ONCE, into state, never `peekHandoff()` on every render. The module
  // store is emptied the instant the exchange succeeds, while the navigation it
  // fires is still in flight — /help/docs is a lazy route, so the router is off
  // fetching a chunk — and signing in re-renders this component (the provider
  // flips to signedIn) inside that window. Reading the store there would find
  // it empty, take the "landed here without a code" branch below, and replace
  // the pending navigation with the console home: the dashboard's Handbook link
  // lands on Channels.
  const [handoff] = useState(peekHandoff);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!handoff || started.current) return;
    started.current = true;
    void (async () => {
      try {
        await signInWithHandoff(handoff.code);
        clearHandoff();
        navigate(handoff.to, { replace: true });
      } catch (e) {
        clearHandoff();
        // The CRM's own message is the actionable one (expired link, wrong
        // tenant) and is written for an operator. Anything else here is a
        // Firebase or service-account fault whose raw text ("Firebase: Error
        // (auth/invalid-custom-token)") tells them nothing they can act on.
        setError(
          e instanceof ApiError
            ? e.message
            : 'Something went wrong completing the sign-in. Sign in below instead.',
        );
      }
    })();
  }, [handoff, signInWithHandoff, navigate]);

  // Landed here without a code — a stale bookmark, or a reload after the URL
  // was cleaned. Whatever session exists already decides where they belong.
  if (!handoff && !error) {
    return <Navigate to={status === 'signedIn' ? '/' : '/signin'} replace />;
  }

  return (
    <div className="signin-wrap">
      <div className="card signin-card">
        <div className="signin-brand">
          <BrandMark className="wordmark-badge signin-mark" />
          <div>
            <h1 className="signin-title">LimoSphere CRM</h1>
            <p className="signin-sub">
              {error ? 'That link could not sign you in.' : 'Signing you in from the dashboard…'}
            </p>
          </div>
        </div>

        {error ? (
          <>
            <ErrorNote error={error} />
            <button
              type="button"
              className="btn btn-primary signin-submit"
              onClick={() => navigate('/signin')}
            >
              Sign in instead
            </button>
          </>
        ) : (
          <div className="signin-busy">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
}
